/**
 * Esqueletos de carregamento: blocos cinza com um brilho passando, no formato da tela que vai
 * aparecer (no lugar do "Carregando..." em texto). O texto para leitores de tela fica num
 * role="status" escondido. Estilos em src/styles/loading.css (a vitrine de conteúdos e as listas
 * usam os delas, de contents.css e listsTable.css); sem animação para quem pede menos movimento.
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
// Classes da vitrine do LeadTalk/LeadMusic e da tabela de listas, para os esqueletos delas terem
// as mesmas medidas.
import '../../features/contents/contents.css';
import '../../features/lists/listsTable.css';

interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  className?: string;
  style?: CSSProperties;
}

export function Skeleton({ width = '100%', height = 14, radius = 8, className = '', style }: SkeletonProps): JSX.Element {
  return (
    <span
      className={`sk ds-esqueleto ${className}`}
      style={{ width, height, borderRadius: radius, ...style }}
      aria-hidden="true"
    />
  );
}

/** Aviso escondido para leitores de tela (o esqueleto em si é decorativo). */
export function LoadingStatus({ label }: { label?: string }): JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="sk-sr" role="status" aria-live="polite">
      {label ?? t('loading', 'Carregando...')}
    </span>
  );
}

/** Título e subtítulo de página, direto sobre o fundo (por isso `sk--fundo`). */
export function PageHeadSkeleton(): JSX.Element {
  return (
    <div className="sk-head">
      <Skeleton width={44} height={44} radius={12} className="sk--fundo" />
      <div className="sk-stack">
        <Skeleton width={220} height={22} className="sk--fundo" />
        <Skeleton width={360} height={12} className="sk--fundo" />
      </div>
    </div>
  );
}

/**
 * Tabela: barra de ações e linhas. O cabeçalho escuro (`head`) só onde a tabela de verdade tem
 * um (leads); nas listas em cartões ele ficava solto em cima do esqueleto.
 */
export function TableSkeleton({
  rows = 8,
  columns = 5,
  actions = true,
  head = false,
}: {
  rows?: number;
  columns?: number;
  actions?: boolean;
  head?: boolean;
}): JSX.Element {
  const widths = ['8%', '28%', '24%', '18%', '14%', '12%'];
  return (
    <div className="sk-table">
      <LoadingStatus />
      {actions ? (
        <div className="sk-table__actions">
          <Skeleton width={180} height={14} />
          <span className="sk-grow" />
          <Skeleton width={96} height={40} radius={10} />
          <Skeleton width={96} height={40} radius={10} />
          <Skeleton width={120} height={40} radius={10} />
        </div>
      ) : null}
      {head ? <div className="sk-table__head" /> : null}
      {Array.from({ length: rows }, (_, row) => (
        <div className="sk-table__row" key={row}>
          {Array.from({ length: columns }, (_, col) => (
            <Skeleton key={col} width={widths[col % widths.length]} height={12} style={{ opacity: 1 - row * 0.07 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Grade de cartões (programa de indicação). */
export function CardGridSkeleton({ cards = 6 }: { cards?: number }): JSX.Element {
  return (
    <div className="sk-grid">
      <LoadingStatus />
      {Array.from({ length: cards }, (_, i) => (
        <div className="sk-card" key={i}>
          <Skeleton height={140} radius={10} />
          <Skeleton width="80%" height={14} />
          <Skeleton width="55%" height={12} />
        </div>
      ))}
    </div>
  );
}

/** Página com título + tabela: o esqueleto das telas sem esqueleto próprio. */
export function ListPageSkeleton(): JSX.Element {
  return (
    <div className="sk-page ds-aparece">
      <PageHeadSkeleton />
      <TableSkeleton />
    </div>
  );
}

/** Página com título + grade de cartões. */
export function CardPageSkeleton(): JSX.Element {
  return (
    <div className="sk-page ds-aparece">
      <PageHeadSkeleton />
      <CardGridSkeleton />
    </div>
  );
}

/**
 * Barra dentro de uma linha de texto: a linha fica com a altura do texto de verdade. O tom
 * depende do que está embaixo: `fundo` direto sobre o fundo da página, `branco` dentro de uma
 * caixa branca (tabela), `dark` no destaque escuro.
 */
function TextBar({
  width,
  height = 12,
  tone = 'fundo',
}: {
  width: number | string;
  height?: number;
  tone?: 'fundo' | 'branco' | 'dark';
}): JSX.Element {
  return (
    <Skeleton
      width={width}
      height={height}
      className={tone === 'dark' ? 'sk--dark' : tone === 'fundo' ? 'sk--fundo' : ''}
      style={{ display: 'inline-block', maxWidth: '100%', verticalAlign: 'middle' }}
    />
  );
}

/** Coluna da tabela de listas: a largura da coluna de verdade e se é número (alinha à direita). */
export interface ListsSkeletonColumn {
  width?: number;
  num?: boolean;
}

/** Colunas por tela, para a abertura do painel (antes de a tela montar as dela). */
const LIST_COLUMNS: Record<string, ListsSkeletonColumn[]> = {
  '/emails': [{ width: 250 }, { width: 100, num: true }],
  '/phones': [{ width: 230 }, { width: 100, num: true }, { width: 104, num: true }],
  '/places/lists': [
    { width: 108, num: true },
    { width: 104, num: true },
    { width: 92, num: true },
    { width: 92, num: true },
  ],
};

/**
 * Telas de listas (Negócios locais, Telefones, Emails): o mesmo esqueleto na abertura do painel e
 * enquanto as listas chegam. Usa as classes da tela (listsTable.css) para ter as mesmas medidas:
 * título e resumo com a busca ao lado; tabela com cabeçalho, um mês e as linhas de 60 px.
 */
export function ListsSkeleton({
  columns = LIST_COLUMNS['/emails']!,
  rows = 7,
}: {
  columns?: ListsSkeletonColumn[];
  rows?: number;
}): JSX.Element {
  return (
    <section className="lt-page">
      <LoadingStatus />
      <div className="lt-head" aria-hidden="true">
        <div>
          <h1>
            <TextBar width={240} height={20} />
          </h1>
          <p>
            <TextBar width={200} height={12} />
          </p>
        </div>
        <div className="lt-search">
          <Skeleton height={42} radius={12} className="sk--fundo" />
        </div>
      </div>
      <div className="lt-box" aria-hidden="true">
        <table className="lt-table">
          <colgroup>
            <col style={{ width: 52 }} />
            <col />
            {columns.map((column, i) => (
              <col key={i} style={{ width: column.width }} />
            ))}
            <col style={{ width: 150 }} />
            <col style={{ width: 104 }} />
          </colgroup>
          <thead>
            <tr>
              <th className="lt-check">
                <Skeleton width={18} height={18} radius={5} />
              </th>
              <th>
                <TextBar width={44} height={10} tone="branco" />
              </th>
              {columns.map((column, i) => (
                <th key={i} className={column.num ? 'lt-th--num' : undefined}>
                  <TextBar width={52} height={10} tone="branco" />
                </th>
              ))}
              <th>
                <TextBar width={70} height={10} tone="branco" />
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr className="lt-group">
              <td colSpan={columns.length + 4}>
                <TextBar width={96} height={10} tone="branco" />
              </td>
            </tr>
            {Array.from({ length: rows }, (_, row) => (
              <tr className="lt-row" key={row} style={{ opacity: 1 - row * 0.08 }}>
                <td className="lt-check">
                  <Skeleton width={18} height={18} radius={5} />
                </td>
                <td className="lt-name">
                  <TextBar width={row % 2 ? '48%' : '64%'} tone="branco" />
                </td>
                {columns.map((column, i) => (
                  <td key={i} className={`lt-cell${column.num ? ' lt-cell--num' : ''}`}>
                    <TextBar width={column.num ? 36 : '72%'} tone="branco" />
                  </td>
                ))}
                <td className="lt-date">
                  <TextBar width={86} tone="branco" />
                </td>
                <td className="lt-actions">
                  {/* Baixar e "…": o ícone de 20 px no botão de 36 px. */}
                  <div>
                    <Skeleton width={20} height={20} radius={6} style={{ margin: 8 }} />
                    <Skeleton width={20} height={20} radius={6} style={{ margin: 8 }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Vitrine do LeadTalk e do LeadMusic: o mesmo esqueleto na abertura do painel e enquanto as
 * playlists chegam. Usa as classes da vitrine (contents.css) para ter as mesmas medidas:
 * título com Tocar tudo (e Aleatório nas músicas), destaque escuro, seções com cartões 16:9.
 */
export function ContentShowcaseSkeleton({ format }: { format: 'leadtalk' | 'leadmusic' }): JSX.Element {
  const talk = format === 'leadtalk';
  const sections = talk ? [5, 5] : [10];
  return (
    <div className="cs">
      <LoadingStatus />
      <div aria-hidden="true">
        <div className="cs-head">
          <div className="cs-head__text">
            <div className="cs-kicker">
              <TextBar width={72} height={11} />
            </div>
            <h1>
              <Skeleton width={40} height={40} radius={10} className="sk--fundo" />
              <Skeleton width={150} height={26} className="sk--fundo" />
            </h1>
            <p>
              <TextBar width={620} height={12} />
            </p>
          </div>
          <div className="cs-head__actions">
            <Skeleton width={132} height={44} radius={10} className="sk--fundo" />
            {talk ? null : <Skeleton width={124} height={44} radius={10} className="sk--fundo" />}
          </div>
        </div>

        <div className="cs-hero">
          <div className="cs-hero__thumb" style={{ background: 'none' }}>
            <Skeleton height="auto" radius={12} className="sk--dark" style={{ aspectRatio: '16 / 9' }} />
          </div>
          <div className="cs-hero__text">
            <Skeleton width={104} height={26} radius={999} className="sk--dark" />
            <h2>
              <TextBar width="88%" height={24} tone="dark" />
              <br />
              <TextBar width="56%" height={24} tone="dark" />
            </h2>
            <p>
              <TextBar width={190} height={12} tone="dark" />
            </p>
            <div className="cs-hero__actions">
              <Skeleton width={150} height={44} radius={10} className="sk--dark" />
              <Skeleton width={140} height={44} radius={10} className="sk--dark" />
            </div>
          </div>
        </div>

        {sections.map((cards, s) => (
          <div className="cs-sec" key={s}>
            <div className="cs-sec__head">
              <h2>
                <TextBar width={170} height={16} />
              </h2>
              <span className="cs-sec__meta">
                <TextBar width={110} height={11} />
              </span>
              <span className="cs-sec__space" />
              {talk ? <Skeleton width={112} height={36} radius={10} className="sk--fundo" /> : null}
            </div>
            <div className="cs-grid">
              {Array.from({ length: cards }, (_, i) => (
                <div className="cs-card" key={i}>
                  <Skeleton height="auto" radius={12} className="sk--fundo" style={{ aspectRatio: '16 / 9' }} />
                  <div className="cs-card__foot">
                    <div className="cs-card__text">
                      <div className="cs-card__title">
                        <TextBar width="92%" />
                        <br />
                        <TextBar width="60%" />
                      </div>
                      <div className="cs-card__dur">
                        <TextBar width={44} height={10} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Esqueleto da página aberta direto pelo endereço, enquanto o painel abre. */
function ShellPageSkeleton(): JSX.Element {
  const path = typeof window === 'undefined' ? '' : window.location.pathname.replace(/\/$/, '');
  if (path === '/leadtalk' || path === '/leadmusic') return <ContentShowcaseSkeleton format={path.slice(1) as 'leadtalk' | 'leadmusic'} />;
  const columns = LIST_COLUMNS[path];
  if (columns) return <ListsSkeleton columns={columns} />;
  return <ListPageSkeleton />;
}

/** Área de mapa (mapa da lista de negócios locais). */
export function MapSkeleton({ height = 420 }: { height?: number }): JSX.Element {
  return (
    <div className="sk-map ds-esqueleto" style={{ height }}>
      <LoadingStatus />
      <span className="material-symbols-rounded sk-map__icon" aria-hidden="true">
        map
      </span>
    </div>
  );
}

/**
 * Abertura do painel (antes do /app/me): o cabeçalho escuro e a faixa da Home, para a página
 * não começar em branco com um texto solto.
 */
export function ShellSkeleton(): JSX.Element {
  const home = typeof window === 'undefined' || window.location.pathname === '/';
  return (
    <div className={`sk-shell${home ? '' : ' sk-shell--page'}`}>
      <LoadingStatus />
      <div className="sk-shell__header">
        <span className="sk sk--dark" style={{ width: 32, height: 32, marginRight: 62, borderRadius: '50%' }} />
        <span className="sk sk--dark" style={{ width: 64, height: 12 }} />
        <span className="sk sk--dark" style={{ width: 64, height: 12 }} />
        <span className="sk sk--dark" style={{ width: 86, height: 12 }} />
        <span className="sk sk--dark" style={{ width: 76, height: 12 }} />
        <span className="sk-grow" />
        <span className="sk sk--dark" style={{ width: 168, height: 40, borderRadius: 8 }} />
      </div>
      {home ? null : (
        <div className="wrapper">
          <ShellPageSkeleton />
        </div>
      )}
      <div className="sk-shell__hero" hidden={!home}>
        <div className="sk-shell__text">
          <span className="sk sk--dark" style={{ width: 340, height: 30 }} />
          <span className="sk sk--dark" style={{ width: 460, height: 12 }} />
          <span className="sk sk--dark" style={{ width: 380, height: 12 }} />
          <div className="sk-shell__tiles">
            {[0, 1, 2].map((i) => (
              <span className="sk sk--dark" key={i} style={{ height: 120, borderRadius: 12 }} />
            ))}
          </div>
        </div>
        <span className="sk-shell__globe" />
      </div>
    </div>
  );
}
